import { ExternalLink, Database } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Dataset {
  id: string;
  title: string;
  description: string;
  external_url: string;
  category: string;
  difficulty: string;
  source: string | null;
  is_active: boolean;
}

interface DatasetCardProps {
  dataset: Dataset;
  isAdmin?: boolean;
  onEdit?: (dataset: Dataset) => void;
  onToggleActive?: (dataset: Dataset) => void;
}

const difficultyColors: Record<string, string> = {
  iniciante: "bg-emerald-100 text-emerald-700",
  intermediario: "bg-yellow-100 text-yellow-700",
  avancado: "bg-red-100 text-red-700",
};

const categoryColors: Record<string, string> = {
  sql: "bg-blue-100 text-blue-700",
  python: "bg-indigo-100 text-indigo-700",
  excel: "bg-green-100 text-green-700",
  "power bi": "bg-orange-100 text-orange-700",
  "machine learning": "bg-purple-100 text-purple-700",
  geral: "bg-gray-100 text-gray-700",
};

export function DatasetCard({ dataset, isAdmin, onEdit, onToggleActive }: DatasetCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-muted-foreground shrink-0" />
            <CardTitle className="text-base leading-tight">{dataset.title}</CardTitle>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${categoryColors[dataset.category.toLowerCase()] || categoryColors.geral}`}>
            {dataset.category}
          </span>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${difficultyColors[dataset.difficulty.toLowerCase()] || difficultyColors.iniciante}`}>
            {dataset.difficulty}
          </span>
          {dataset.source && (
            <Badge variant="outline" className="text-xs">
              {dataset.source}
            </Badge>
          )}
          {!dataset.is_active && (
            <Badge variant="destructive" className="text-xs">Inativo</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
          {dataset.description}
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" className="gap-1.5" onClick={async () => {
            try {
              const { data: { user } } = await supabase.auth.getUser();
              await (supabase.from('dataset_clicks') as any).insert({
                dataset_id: dataset.id,
                user_id: user?.id || null,
              });
            } catch (e) {
              console.error('Error tracking dataset click:', e);
            }
            window.open(dataset.external_url, '_blank', 'noopener,noreferrer');
          }}>
            <ExternalLink className="h-3.5 w-3.5" />
            Acessar Dataset
          </Button>
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" onClick={() => onEdit?.(dataset)}>
                Editar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onToggleActive?.(dataset)}
              >
                {dataset.is_active ? "Desativar" : "Ativar"}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
